import { useTranslation } from "react-i18next";
import {
  IContractDetailsResponseData,
  TransactionStatus,
} from "../../../setup/api/services/contract/models";
import { TABLE_HEADERS } from "../constants";
import styles from "./styles.module.scss";
import { useEffect, useMemo, useState } from "react";
import { AlertColor } from "@mui/material";
import CustomizedSnackbars from "../../../components/common/customizedSnackbars";
import { ROUTES } from "../../../setup/constants";
import StatusPill from "../CashoutCard/StatusPill";

interface ICashoutsListItem {
  contract: IContractDetailsResponseData;
}

const CashoutsListItem = ({ contract }: ICashoutsListItem) => {
  const { t } = useTranslation();

  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>("info");
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");

  const contractCashoutStatus: TransactionStatus = useMemo(() => {
    const cashOutPartiesCount = contract?.cashOutAmountList?.length ?? 0;
    const set = new Set();
    contract?.transactions.forEach((transaction) => {
      if (transaction.beneficiaryId && !set.has(transaction.beneficiaryId)) {
        set.add(transaction.beneficiaryId);
      }
    });
    const transactionPartiesCount = set.size;
    if (transactionPartiesCount < cashOutPartiesCount) {
      return TransactionStatus.NOT_SENT;
    }
    const contractCashoutTransactions = contract?.transactions.filter(
      (transaction) => {
        return (
          transaction.type === "CASH_OUT" ||
          transaction.type === "REFUND_CUSTOMER" ||
          transaction.type === "RELEASE_TO_PROVIDER" ||
          transaction.type === "CLOSE_RELEASE_TO_PROVIDER"
        );
      }
    );
    if (contractCashoutTransactions?.length) {
      const lastTransaction =
        contractCashoutTransactions[contractCashoutTransactions.length - 1];
      return lastTransaction.status;
    }
    return TransactionStatus.NOT_SENT;
  }, [contract]);

  const createRow = (desc: string, value) => {
    return { desc, value };
  };

  const handleRowClick = () => {
    window.open(`${ROUTES.CASHOUTS}/${contract.id}`, "_blank");
  };

  const onCopy = async () => {
    try {
      await navigator?.clipboard?.writeText(contract.shortId);
      setSnackBarOpen(true);
      setSnackBarColor("success");
      setSnackBarTitle(t("COPIED"));
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBarColor("error");
      setSnackBarTitle(t("COPIED"));
      console.error(error);
    }
  };

  const rows = [
    createRow(
      TABLE_HEADERS.TRANSACTION_NAME,
      <div className={styles["transaction_name"]} onClick={handleRowClick}>
        <p dir="rtl">{contract.itemDetail.title || contract.id || ""}</p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.TRANSACTION_ID,
      <div className={styles["transaction_id"]}>
        <p dir="rtl">{contract.shortId || ""}</p>
        <img src="/assets/images/svg/copy.svg" alt="" onClick={onCopy} />
      </div>
    ),
    createRow(
      TABLE_HEADERS.CASHOUTS_NUMBER,
      <div className={styles["cashouts_number"]}>
        <p dir="rtl">
          {contract.cashOutAmountList ? contract.cashOutAmountList.length : "0"}
        </p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.TOTAL_AMOUNT,
      <div className={styles["cashouts_number"]}>
        <p dir="rtl">
          {(contract.cashOutAmountList
            ? contract.cashOutAmountList
                .reduce((acc, item) => acc + item.cashOutAmount, 0)
                .toFixed(2)
            : "0") +
            " " +
            t("lbl_saudi_currency")}
        </p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.STATUS,
      <div className={styles["status"]}>
        <StatusPill status={contractCashoutStatus} />
      </div>
    ),
  ];

  useEffect(() => {
    if (snackBarOpen) {
      setTimeout(() => {
        setSnackBarOpen(false);
        setSnackBarColor("info");
        setSnackBarTitle("");
      }, 5000);
    }
  }, [snackBarOpen]);

  const renderSnackBar = () => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  };

  return (
    <>
      <tr className={styles.table_row}>
        {rows.map((row, index) => (
          <td className={styles.table_value} key={index}>
            {row.value}
          </td>
        ))}
      </tr>
      <div className={styles.snackbar}>{renderSnackBar()}</div>
    </>
  );
};

export default CashoutsListItem;
